import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { boardCells, boardSize } from "../features/hex/board";
import { HexBoard } from "../features/hex/HexBoard";
import { submit } from "../lib/runtime";
import { useCanvasState } from "../lib/useCanvasState";

/** The moderator canvas: the same board in hard colours, plus the controls. */
export const Route = createFileRoute("/hex/moderator/$choiceMount/$boardMount")({
  component: ModeratorCanvas,
});

function ModeratorCanvas() {
  const { choiceMount, boardMount } = Route.useParams();
  const choices = useCanvasState(choiceMount);
  const game = useCanvasState(boardMount);
  const [refused, setRefused] = useState<string | null>(null);

  const cells = boardCells(choices.state, game.state);
  const { columns, rows } = boardSize([
    ...(choices.state?.entities ?? []),
    ...(game.state?.entities ?? []),
  ]);

  if (game.error !== null) return <Problem what={game.error} />;

  const blocked = [...cells.entries()].filter(([, one]) => one.blocked).map(([id]) => id);
  const selected = [...cells.entries()].find(([, one]) => one.selected)?.[0] ?? "nothing";

  return (
    <main style={{ position: "fixed", inset: 0, background: "#0b0d10" }}>
      {columns > 0 && (
        <HexBoard columns={columns} rows={rows} cells={cells} look="control" />
      )}
      <div
        style={{
          position: "absolute",
          right: 12,
          top: 12,
          display: "flex",
          gap: 8,
          alignItems: "center",
          font: "13px ui-monospace, monospace",
          color: "rgba(255,255,255,0.75)",
        }}
      >
        <span>leading {selected}</span>
        <span>blocked {blocked.length}</span>
        {refused !== null && <span style={{ color: "#ff8a80" }}>{refused}</span>}
        <button
          type="button"
          onClick={() => {
            setRefused(null);
            // A refusal has to reach the person who pressed it. Discarding this promise is what
            // made a control that the cloud was rejecting look like a control that did nothing.
            submit(boardMount, "force_clear", "all").catch((error: unknown) => {
              setRefused(error instanceof Error ? error.message : "the clear was refused");
            });
          }}
          style={{
            padding: "8px 14px",
            cursor: "pointer",
            background: "#c0392b",
            color: "white",
            border: 0,
            borderRadius: 4,
            font: "inherit",
          }}
        >
          force clear
        </button>
      </div>
    </main>
  );
}

function Problem({ what }: { what: string }) {
  return (
    <main style={{ padding: 24, fontFamily: "monospace", color: "#eee", background: "#0b0d10" }}>
      <h1>Cannot read this run</h1>
      <p>{what}</p>
      <p>Sign in first at /v1/auth/twitch/start?as=moderator, then reload.</p>
    </main>
  );
}
